import { ref, shallowRef, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAuthStore } from './auth.store';
import notificationsService from '@/shared/services/notifications.service';
import { CanceledError } from 'axios';

// The bell count belongs to saved recipients, not native push receipt or current clinical flags.
export const useNotificationsStore = defineStore('notificationsStore', () => {
  const auth = useAuthStore();
  const unreadCount = shallowRef(0);
  const centerOpen = shallowRef(false);
  // Detail prose refreshes in place so pending responses retain their selection owner.
  const selected = ref(null);
  let countRequest = 0;

  async function find(params = {}) {
    const request = ++countRequest;
    const result = await notificationsService.find(params);
    if (request === countRequest) {
      unreadCount.value = result.unreadCount;
    }
    return result;
  }

  async function refreshCount() {
    if (auth.isLoggedIn) {
      const request = ++countRequest;
      const result = await notificationsService.getUnreadCount();
      if (request === countRequest) {
        unreadCount.value = result.unreadCount;
      }
    }
  }

  async function read(itemUnique) {
    const version = auth.authStateVersion;
    // A list requested before this action must not restore the previous unread count.
    countRequest += 1;
    const result = await notificationsService.read(itemUnique);
    if (version !== auth.authStateVersion) {
      throw new CanceledError();
    }
    await refreshCount();
    return result;
  }

  watch(
    () => auth.authStateVersion,
    () => {
      countRequest += 1;
      unreadCount.value = 0;
      centerOpen.value = false;
      selected.value = null;
    },
    { flush: 'sync' },
  );
  return { unreadCount, centerOpen, selected, find, read, refreshCount };
});
